/**
 * Admin (foundation routes; generated types from schema.ts).
 *
 *   tenants:   GET/POST /api/admin/tenants, GET/PATCH/DELETE /api/admin/tenants/{id}
 *   users:     GET /api/admin/users, PATCH /api/admin/users/{id}
 *   invites:   GET/POST /api/admin/invites, DELETE /api/admin/invites/{id}
 *   uploads:   GET /api/admin/uploads/datasets, POST /api/admin/uploads/{dataset}
 *   schedules: GET /api/admin/schedules, PATCH /api/admin/schedules/{name}, POST .../run
 *   audit:     GET /api/admin/audit
 */
import { api, type Schemas } from "./client";

export type TenantOut = Schemas["TenantOut"];
export type TenantDetailOut = Schemas["TenantDetailOut"];
export type TenantIn = Schemas["TenantIn"];
export type TenantPatch = Schemas["TenantPatch"];
export type UserOut = Schemas["UserOut"];
export type UserPatch = Schemas["UserPatch"];
export type InviteIn = Schemas["InviteIn"];
export type InviteOut = Schemas["InviteOut"];
export type InviteCreatedOut = Schemas["InviteCreatedOut"];
export type UploadOut = Schemas["crabond__api__admin__UploadOut"];
export type ScheduleOut = Schemas["ScheduleOut"];
export type SchedulePatch = Schemas["SchedulePatch"];
export type ScheduleRunOut = Schemas["ScheduleRunOut"];
export type AuditOut = Schemas["AuditOut"];

export const adminKeys = {
  tenants: ["admin", "tenants"] as const,
  users: (tenantId?: number | null) => ["admin", "users", tenantId ?? null] as const,
  invites: ["admin", "invites"] as const,
  datasets: ["admin", "uploads", "datasets"] as const,
  schedules: ["admin", "schedules"] as const,
  audit: ["admin", "audit"] as const,
};

export const listTenants = () => api.get<TenantOut[]>("/api/admin/tenants");
export const createTenant = (body: TenantIn) => api.post<TenantDetailOut>("/api/admin/tenants", body);
export const updateTenant = (id: number, body: TenantPatch) =>
  api.patch<TenantDetailOut>(`/api/admin/tenants/${id}`, body);
export const deactivateTenant = (id: number) => api.delete(`/api/admin/tenants/${id}`);

export const listUsers = (tenantId?: number | null) =>
  api.get<UserOut[]>("/api/admin/users", { tenant_id: tenantId ?? undefined });
export const updateUser = (id: number, body: UserPatch) =>
  api.patch<UserOut>(`/api/admin/users/${id}`, body);

export const listInvites = (pending = true) =>
  api.get<InviteOut[]>("/api/admin/invites", { pending });
export const createInvite = (body: InviteIn) =>
  api.post<InviteCreatedOut>("/api/admin/invites", body);
export const revokeInvite = (id: number) => api.delete(`/api/admin/invites/${id}`);

export const listUploadDatasets = () =>
  api.get<Schemas["DatasetsOut"]>("/api/admin/uploads/datasets").then((d) => d.datasets);

export function uploadDataset(
  dataset: string,
  file: File,
  opts: { year?: number | null; vintage?: string | null; params?: Record<string, unknown> | null } = {},
): Promise<UploadOut> {
  const fd = new FormData();
  fd.set("file", file, file.name);
  if (opts.year != null) fd.set("year", String(opts.year));
  if (opts.vintage) fd.set("vintage", opts.vintage);
  if (opts.params && Object.keys(opts.params).length > 0) fd.set("params", JSON.stringify(opts.params));
  return api.upload<UploadOut>(`/api/admin/uploads/${encodeURIComponent(dataset)}`, fd);
}

export const listSchedules = () => api.get<ScheduleOut[]>("/api/admin/schedules");
export const updateSchedule = (name: string, body: SchedulePatch) =>
  api.patch<ScheduleOut>(`/api/admin/schedules/${encodeURIComponent(name)}`, body);
export const runSchedule = (name: string) =>
  api.post<ScheduleRunOut>(`/api/admin/schedules/${encodeURIComponent(name)}/run`);

export const listAudit = (limit = 100, action?: string) =>
  api.get<AuditOut[]>("/api/admin/audit", { limit, action });

/**
 * Upload-capable datasets (DESIGN §10). Dataset ids are snake_case job suffixes (`etl.<id>`);
 * the server's /uploads/datasets list is authoritative and these only add labels and hints.
 */
export const UPLOAD_DATASETS: Record<string, { label: string; hint: string; needsYear?: boolean }> = {
  ffiec_census: {
    label: "FFIEC census flat file",
    hint: "CensusFlatFile{YYYY}.zip; set the processing year. Definitions .xlsx can be a second upload.",
    needsYear: true,
  },
  ffiec_cra_flat: {
    label: "FFIEC CRA disclosure / transmittal",
    hint: "{yy}exp_discl.zip or {yy}exp_trans.zip (D6 AA definitions, D5 CD loans).",
    needsYear: true,
  },
  fannie_dus: {
    label: "Fannie Mae DUS Disclose",
    hint: "Advanced Search “Download MBS Results” (.txt), Monthly New Issuance (.csv), or a .zip of several. Pick several files at once; a pool in two files keeps the last one loaded.",
  },
  ginnie_mf: { label: "Ginnie Mae MF Pool & Loan V4", hint: "mfplmon4_YYYYMM.zip (pipe-delimited)." },
  msia_export: { label: "MSIA K-deal export", hint: "Annex A property export (CSV/XLSX)." },
  pe: {
    label: "Performance evaluation PDF",
    hint: "A CRA PE PDF for peer extraction. Keep the agency file name, or pass params {fdic_cert, exam_date} or {cra_exam_id}.",
  },
};

/** True when any scheduled refresh last failed; the Admin page shows a red banner. */
export function failedSchedules(rows: ScheduleOut[] | undefined): ScheduleOut[] {
  return (rows ?? []).filter((r) => r.last_status === "FAILED");
}
